import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { resolveMediaUrl } from '../../utils/media';

const Layout = styled.div`
  display: grid;
  grid-template-columns: 240px 1fr;
  height: 100vh;
  width: 100vw;
  background: #eef1f6;
  font-family: 'Space Grotesk', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
`;

const Sidebar = styled.aside`
  background: #0f172a;
  color: #cbd5f5;
  padding: 32px 24px;
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const Brand = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: #f8fafc;
  letter-spacing: 0.05em;
`;

const NavSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const NavLabel = styled.span`
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #94a3b8;
`;

const NavButton = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px 14px;
  border-radius: 14px;
  border: none;
  background: ${(props) => (props.$active ? 'rgba(59, 130, 246, 0.18)' : 'transparent')};
  color: inherit;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.01em;
  cursor: pointer;
  transition: background 0.2s ease, transform 0.2s ease;

  &:hover {
    background: rgba(59, 130, 246, 0.22);
    transform: translateX(4px);
  }
`;

const IconBadge = styled.span`
  width: 40px;
  height: 40px;
  border-radius: 14px;
  background: ${(props) => (props.$active ? 'rgba(255, 255, 255, 0.16)' : 'rgba(148, 163, 184, 0.12)')};
  display: flex;
  align-items: center;
  justify-content: center;
  color: #f8fafc;
  flex-shrink: 0;

  svg {
    display: block;
  }
`;

const NavText = styled.span`
  color: #e2e8f0;
  font-size: 14px;
`;

const NavIndicator = styled.span`
  margin-left: auto;
  width: 10px;
  height: 10px;
  border-radius: 999px;
  background: #38bdf8;
  box-shadow: 0 0 0 6px rgba(56, 189, 248, 0.15);
`;

const Main = styled.main`
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const Topbar = styled.header`
  height: 72px;
  padding: 0 32px;
  background: #fff;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const SearchBox = styled.div`
  flex: 1;
  max-width: 420px;
  margin-left: 24px;
  position: relative;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 12px 16px 12px 44px;
  border-radius: 16px;
  border: 1px solid #e2e8f0;
  font-size: 14px;
  outline: none;

  &:focus {
    border-color: #94a3b8;
    box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
  }
`;

const SearchIcon = styled.span`
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  font-size: 14px;
`;

const ProfileCluster = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Avatar = styled.div`
  width: 42px;
  height: 42px;
  border-radius: 50%;
  background: #0f172a;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  overflow: hidden;
`;

const AvatarImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const ProfileMenu = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const ProfileButton = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  border-radius: 999px;
  border: 1px solid transparent;
  background: transparent;
  cursor: pointer;
  transition: border 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;

  &:hover {
    border-color: #e2e8f0;
    background: #f8fafc;
    box-shadow: 0 12px 28px rgba(15, 23, 42, 0.12);
  }
`;

const Caret = styled.span`
  width: 0;
  height: 0;
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  border-top: 6px solid #94a3b8;
  transform: ${(props) => (props.$open ? 'rotate(180deg)' : 'none')};
  transition: transform 0.2s ease;
`;

const DropdownMenu = styled.div`
  position: absolute;
  right: 0;
  top: calc(100% + 12px);
  width: 180px;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 24px 55px rgba(15, 23, 42, 0.15);
  border: 1px solid #e2e8f0;
  padding: 8px;
  z-index: 20;
`;

const DropdownItem = styled.button`
  width: 100%;
  border: none;
  background: transparent;
  color: ${(props) => (props.$danger ? '#dc2626' : '#0f172a')};
  padding: 10px 12px;
  border-radius: 12px;
  font-size: 14px;
  text-align: left;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: #f8fafc;
  }
`;

const DashboardLayout = ({
  admin,
  avatar,
  navItems = [],
  brand = 'Finance Advisor',
  pageTitle = 'Dashboard',
  searchPlaceholder = 'Search clients, portfolios, alerts',
  onViewProfile,
  onLogout,
  children
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef(null);

  const fullName = useMemo(() => {
    if (admin?.full_name && admin.full_name.trim()) {
      return admin.full_name.trim();
    }

    if (admin?.username) {
      return admin.username;
    }

    return 'Admin';
  }, [admin]);

  const initials = useMemo(() => {
    if (admin?.full_name) {
      return admin.full_name
        .split(' ')
        .filter(Boolean)
        .map((part) => part[0])
        .join('')
        .slice(0, 2)
        .toUpperCase();
    }

    if (admin?.username) {
      return admin.username.slice(0, 2).toUpperCase();
    }

    return 'AD';
  }, [admin]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const closeMenu = () => setIsMenuOpen(false);

  const handleViewProfileClick = () => {
    closeMenu();
    onViewProfile?.();
  };

  const handleLogoutClick = () => {
    closeMenu();
    onLogout?.();
  };

  const avatarSrc = useMemo(() => resolveMediaUrl(avatar || admin?.image_url), [avatar, admin?.image_url]);

  return (
    <Layout>
      <Sidebar>
        <Brand>{brand}</Brand>
        {navItems.length > 0 && (
          <NavSection>
            <NavLabel>Navigation</NavLabel>
            {navItems.map((item) => (
              <NavButton
                key={item.label}
                $active={item.active}
                type="button"
                onClick={item.onClick}
                aria-current={item.active ? 'page' : undefined}
              >
                {item.Icon && (
                  <IconBadge $active={item.active} aria-hidden="true">
                    <item.Icon />
                  </IconBadge>
                )}
                <NavText>{item.label}</NavText>
                {item.active && <NavIndicator aria-hidden="true" />}
              </NavButton>
            ))}
          </NavSection>
        )}
      </Sidebar>

      <Main>
        <Topbar>
          <div style={{ fontWeight: 600, fontSize: '18px', color: '#0f172a' }}>{pageTitle}</div>
          <SearchBox>
            <SearchInput placeholder={searchPlaceholder} />
            <SearchIcon aria-hidden="true">⌕</SearchIcon>
          </SearchBox>
          <ProfileMenu ref={menuRef}>
            <ProfileButton
              type="button"
              onClick={() => setIsMenuOpen((prev) => !prev)}
              aria-haspopup="menu"
              aria-expanded={isMenuOpen}
            >
              <ProfileCluster>
                <Avatar>
                  {avatarSrc ? <AvatarImage src={avatarSrc} alt={`${fullName} avatar`} /> : initials}
                </Avatar>
                <div>
                  <div style={{ fontWeight: 600, color: '#0f172a' }}>{fullName}</div>
                </div>
              </ProfileCluster>
              <Caret $open={isMenuOpen} />
            </ProfileButton>

            {isMenuOpen && (
              <DropdownMenu role="menu">
                {onViewProfile && (
                  <DropdownItem type="button" onClick={handleViewProfileClick}>
                    View profile
                  </DropdownItem>
                )}
                {onLogout && (
                  <DropdownItem type="button" onClick={handleLogoutClick} $danger>
                    Log out
                  </DropdownItem>
                )}
              </DropdownMenu>
            )}
          </ProfileMenu>
        </Topbar>
        {children}
      </Main>
    </Layout>
  );
};

export default DashboardLayout;
